import { useEffect } from 'react';
import { isJobsSiteOrigin, publicSiteName, publicSiteUrl } from '@/lib/jobsSite';

interface SeoMetaProps {
  title: string;
  description: string;
  keywords?: string;
  alternateLanguages?: Array<{ hrefLang: string; href: string }>;
  path?: string;
  imagePath?: string;
  siteUrl?: string;
  author?: string;
  publisher?: string;
  robots?: string;
  structuredData?: Record<string, unknown> | Array<Record<string, unknown>>;
}

// On Peoply Kariyer's own domain the tags name that domain, never peoply.ai (see publicSiteUrl).
const defaultTitle = () => (isJobsSiteOrigin() ? publicSiteName() : 'Peoply - HR Management System');
const SEO_ATTRIBUTE = 'data-peoply-seo';

function upsertMeta(selector: string, attributes: Record<string, string>, content: string) {
  let meta = document.head.querySelector<HTMLMetaElement>(selector);

  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(SEO_ATTRIBUTE, 'true');
    document.head.appendChild(meta);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    meta.setAttribute(key, value);
  });
  meta.setAttribute('content', content);
}

function upsertCanonical(href: string) {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');

  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute(SEO_ATTRIBUTE, 'true');
    document.head.appendChild(link);
  }

  link.setAttribute('href', href);
}

function upsertAlternateLanguage(hrefLang: string, href: string) {
  let link = document.head.querySelector<HTMLLinkElement>(`link[rel="alternate"][hreflang="${hrefLang}"]`);

  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'alternate');
    link.setAttribute('hreflang', hrefLang);
    link.setAttribute(SEO_ATTRIBUTE, 'true');
    document.head.appendChild(link);
  }

  link.setAttribute('href', href);
}

function appendStructuredData(structuredData: SeoMetaProps['structuredData']) {
  document.head.querySelectorAll(`script[type="application/ld+json"][${SEO_ATTRIBUTE}="true"]`).forEach((element) => {
    element.remove();
  });

  if (!structuredData) {
    return;
  }

  const entries = Array.isArray(structuredData) ? structuredData : [structuredData];

  entries.forEach((entry) => {
    const script = document.createElement('script');
    script.setAttribute('type', 'application/ld+json');
    script.setAttribute(SEO_ATTRIBUTE, 'true');
    script.textContent = JSON.stringify(entry);
    document.head.appendChild(script);
  });
}

export function SeoMeta({
  title,
  description,
  keywords,
  alternateLanguages,
  path = '/',
  imagePath = '/peoply-logo.svg',
  siteUrl = publicSiteUrl(),
  author = 'Peoply',
  publisher = 'Codefirst Yazılım Teknolojileri A.Ş.',
  robots = 'index, follow',
  structuredData,
}: SeoMetaProps) {
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const pageUrl = new URL(path, siteUrl).toString();
    const imageUrl = new URL(imagePath, siteUrl).toString();

    document.title = title;

    upsertMeta('meta[name="description"]', { name: 'description' }, description);
    if (keywords) {
      upsertMeta('meta[name="keywords"]', { name: 'keywords' }, keywords);
    }
    upsertMeta('meta[name="robots"]', { name: 'robots' }, robots);
    upsertMeta('meta[name="author"]', { name: 'author' }, author);
    upsertMeta('meta[name="publisher"]', { name: 'publisher' }, publisher);
    upsertMeta('meta[property="og:type"]', { property: 'og:type' }, 'website');
    upsertMeta('meta[property="og:locale"]', { property: 'og:locale' }, 'tr_TR');
    upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name' }, publicSiteName());
    upsertMeta('meta[property="og:title"]', { property: 'og:title' }, title);
    upsertMeta('meta[property="og:description"]', { property: 'og:description' }, description);
    upsertMeta('meta[property="og:url"]', { property: 'og:url' }, pageUrl);
    upsertMeta('meta[property="og:image"]', { property: 'og:image' }, imageUrl);
    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card' }, 'summary_large_image');
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title' }, title);
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description' }, description);
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image' }, imageUrl);
    upsertMeta('meta[name="twitter:url"]', { name: 'twitter:url' }, pageUrl);
    upsertCanonical(pageUrl);
    alternateLanguages?.forEach(({ hrefLang, href }) => {
      upsertAlternateLanguage(hrefLang, href);
    });
    appendStructuredData(structuredData);

    return () => {
      document.title = defaultTitle();

      document.head.querySelectorAll(`[${SEO_ATTRIBUTE}="true"]`).forEach((element) => {
        element.remove();
      });
    };
  }, [
    alternateLanguages,
    author,
    description,
    imagePath,
    keywords,
    path,
    publisher,
    robots,
    siteUrl,
    structuredData,
    title,
  ]);

  return null;
}
